import {
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/registry';
import { cn } from '../lib/cn';
import { pushLayer } from '../lib/layers';
import { disabledStyles, focusRing, focusRingOnDark } from '../lib/styles';
import { useLayerRoot } from './Drawer';
import { useFieldControl, type FieldSize } from './Field';

export interface DropdownOption<T extends string = string> {
    value: T;
    label: string;
    /** A quieter second line, e.g. the role next to a name. */
    hint?: string;
    disabled?: boolean;
}

/**
 * A last row under the options that does something instead of choosing a value,
 * such as "Inspecteur uitnodigen". It is reached like an option, by pointer and by
 * keyboard; choosing it closes the list and calls `onSelect`.
 */
export interface DropdownAction {
    label: string;
    /** Brand-sheet icon before the label (`add`). */
    icon?: IconName;
    onSelect: () => void;
}

/**
 * - `light` — on a card or the filter bar, the same shell as `Input`.
 * - `onDark` — in a dark screen header, next to `Button variant="onDark"`.
 *   The open list is light on both.
 */
export type DropdownSurface = 'light' | 'onDark';

export interface DropdownProps<T extends string = string> {
    value: T;
    options: readonly DropdownOption<T>[];
    onChange: (value: T) => void;
    /** Accessible name when there is no `Field` label around it. */
    'aria-label'?: string;
    /** A word before the value: "Sorteer" shows "Sorteer: Opnamedatum". */
    prefix?: string;
    /** Brand-sheet icon before the text (`filter`, `sort`). */
    icon?: IconName;
    /** 42 · 44 · 46px, like `Input`. Inside a `Field` it follows the field. */
    size?: FieldSize;
    surface?: DropdownSurface;
    /** Shown while no option matches `value`. */
    placeholder?: string;
    disabled?: boolean;
    /** Fill the container. Default: inside a `Field` yes, in a toolbar no. */
    fullWidth?: boolean;
    /** Which edge of the button the open list lines up with. */
    align?: 'start' | 'end';
    /** A row under the options that starts something, e.g. inviting a colleague. */
    action?: DropdownAction;
    id?: string;
    className?: string;
}

const triggerSize: Record<FieldSize, string> = {
    sm: 'min-h-[42px] gap-2 pl-3 pr-[10px] text-[13px] rounded-[7px]',
    md: 'min-h-[44px] gap-2 pl-3 pr-[10px] text-[14px] rounded-[7px]',
    lg: 'min-h-[46px] gap-[10px] pl-[14px] pr-3 text-[15px] rounded-[8px]',
};

const optionSize: Record<FieldSize, string> = {
    sm: 'min-h-[36px] text-[13px]',
    md: 'min-h-[40px] text-[14px]',
    lg: 'min-h-[44px] text-[15px]',
};

const surfaceStyles: Record<DropdownSurface, { rest: string; open: string; focus: string }> = {
    light: {
        rest: cn(
            'border border-[var(--border-control)] bg-[var(--surface-card)] text-[var(--text-primary)]',
            'hover:border-[var(--accent)] disabled:bg-[var(--surface-subtle)]',
            'aria-[invalid=true]:border-[var(--danger)]',
        ),
        open: 'border-[var(--accent)]',
        focus: focusRing,
    },
    onDark: {
        rest: cn(
            'border border-[var(--ink-500)] bg-transparent text-[var(--text-on-dark-2)]',
            'hover:bg-[var(--ink-hover-on-dark)]',
        ),
        open: 'bg-[var(--ink-hover-on-dark)]',
        focus: focusRingOnDark,
    },
};

/** Gap between button and list, and the minimum distance to the window edge. */
const GAP = 4;
const EDGE = 8;
const MAX_LIST_HEIGHT = 320;
const TYPEAHEAD_MS = 600;

/**
 * A select-only combobox in the design-system style: a button with the chosen
 * value and a chevron, and a floating list with the options.
 *
 * Use it for the filters and sort orders in office toolbars and for choices in
 * office panels. Inside the opname and on a tablet keep the native `Select`:
 * the platform picker is faster on site.
 *
 * Keyboard, as in the WAI-ARIA select-only combobox: ↓ ↑ Enter or Space open the
 * list on the current value; ↓ ↑ Home End move; Enter or Space choose; Escape
 * closes without choosing (only the list, not a drawer around it); Tab closes and
 * moves on; typing letters jumps to the option that starts with them.
 */
export function Dropdown<T extends string>({
    value,
    options,
    onChange,
    'aria-label': ariaLabel,
    prefix,
    icon,
    size,
    surface = 'light',
    placeholder,
    disabled = false,
    fullWidth,
    align = 'start',
    action,
    id,
    className,
}: DropdownProps<T>) {
    const field = useFieldControl(size);
    const layerRoot = useLayerRoot();
    const listId = useId();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const typed = useRef({ text: '', at: 0 });
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);

    /* The action row comes after the options and takes the next index. */
    const actionIndex = action ? options.length : -1;
    const count = options.length + (action ? 1 : 0);
    const selectedIndex = options.findIndex((option) => option.value === value);
    const selected = selectedIndex === -1 ? null : options[selectedIndex];
    const stretch = fullWidth ?? field.inField;
    const styles = surfaceStyles[surface];
    const optionId = (index: number) => `${listId}-option-${index}`;

    function enabled(index: number): boolean {
        if (index === actionIndex) return true;

        const option = options[index];

        return option !== undefined && !option.disabled;
    }

    function firstEnabled(from: number, step: 1 | -1): number {
        for (let index = from; index >= 0 && index < count; index += step) {
            if (enabled(index)) return index;
        }

        return -1;
    }

    function openList(at: number) {
        if (disabled || count === 0) return;

        setActive(enabled(at) ? at : firstEnabled(0, 1));
        setOpen(true);
    }

    function close() {
        setOpen(false);
        typed.current = { text: '', at: 0 };
    }

    function choose(index: number) {
        if (action && index === actionIndex) {
            close();
            triggerRef.current?.focus();
            action.onSelect();

            return;
        }

        const option = options[index];

        if (!option || option.disabled) return;

        close();
        triggerRef.current?.focus();

        if (option.value !== value) {
            onChange(option.value);
        }
    }

    function move(step: 1 | -1) {
        const from = active === -1 ? (step === 1 ? 0 : count - 1) : active + step;
        const next = firstEnabled(from, step);

        if (next !== -1) setActive(next);
    }

    function typeahead(char: string): number {
        const now = Date.now();
        const text = now - typed.current.at > TYPEAHEAD_MS ? char : typed.current.text + char;
        const needle = text.toLowerCase();
        const current = open ? active : selectedIndex;
        /* A single letter cycles through the options with that letter; a word
           keeps the current match as long as it still fits. */
        const start = current === -1 ? 0 : current + (text.length === 1 ? 1 : 0);

        typed.current = { text, at: now };

        if (options.length === 0) return -1;

        for (let step = 0; step < options.length; step++) {
            const index = (start + step) % options.length;

            if (enabled(index) && options[index]?.label.toLowerCase().startsWith(needle)) {
                return index;
            }
        }

        return -1;
    }

    function onKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
        if (disabled) return;

        const typing = Date.now() - typed.current.at <= TYPEAHEAD_MS && typed.current.text !== '';

        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                event.preventDefault();

                if (!open) {
                    openList(selectedIndex);
                } else if (event.altKey) {
                    choose(active);
                } else {
                    move(event.key === 'ArrowDown' ? 1 : -1);
                }
                return;
            }
            case 'Home':
            case 'End': {
                event.preventDefault();

                const index =
                    event.key === 'Home' ? firstEnabled(0, 1) : firstEnabled(count - 1, -1);

                if (open) {
                    setActive(index);
                } else {
                    openList(index);
                }
                return;
            }
            case 'Enter': {
                event.preventDefault();

                if (open) {
                    choose(active);
                } else {
                    openList(selectedIndex);
                }
                return;
            }
            case ' ': {
                if (typing) break;

                event.preventDefault();

                if (open) {
                    choose(active);
                } else {
                    openList(selectedIndex);
                }
                return;
            }
            case 'Tab': {
                if (open) close();
                return;
            }
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const match = typeahead(event.key);

            if (event.key === ' ') {
                event.preventDefault();
            }

            if (match === -1) return;

            if (open) {
                setActive(match);
            } else {
                openList(match);
            }
        }
    }

    /* Escape closes the list, and only the list — a drawer around it stays open. */
    useEffect(() => {
        if (!open) return;

        return pushLayer({
            modal: false,
            element: listRef.current,
            onEscape: () => {
                setOpen(false);
                triggerRef.current?.focus();
            },
        });
    }, [open]);

    /* A press anywhere outside the button and the list closes it. */
    useEffect(() => {
        if (!open) return;

        function onPointerDown(event: PointerEvent) {
            const target = event.target;

            if (!(target instanceof Node)) return;
            if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return;

            setOpen(false);
        }

        document.addEventListener('pointerdown', onPointerDown, true);

        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);

    /* Under the button, or above it when there is more room there; follows scrolling. */
    useLayoutEffect(() => {
        if (!open) return;

        function place() {
            const trigger = triggerRef.current;
            const list = listRef.current;

            if (!trigger || !list) return;

            const rect = trigger.getBoundingClientRect();
            const below = window.innerHeight - rect.bottom - GAP - EDGE;
            const above = rect.top - GAP - EDGE;

            list.style.minWidth = `${rect.width}px`;
            list.style.maxHeight = `${MAX_LIST_HEIGHT}px`;

            const upward = list.offsetHeight > below && above > below;
            const room = Math.max(120, Math.min(MAX_LIST_HEIGHT, upward ? above : below));

            list.style.maxHeight = `${room}px`;

            const height = list.offsetHeight;
            const width = list.offsetWidth;
            const left = align === 'end' ? rect.right - width : rect.left;

            list.style.left = `${Math.max(EDGE, Math.min(left, window.innerWidth - width - EDGE))}px`;
            list.style.top = `${upward ? rect.top - GAP - height : rect.bottom + GAP}px`;
        }

        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);

        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, align, count]);

    /* Keep the highlighted option in view while you walk the list. */
    useEffect(() => {
        if (!open || active === -1) return;

        document
            .getElementById(`${listId}-option-${active}`)
            ?.scrollIntoView?.({ block: 'nearest' });
    }, [open, active, listId]);

    const label = selected?.label ?? placeholder ?? '';
    const effectiveSize = field.size;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                id={id ?? field.id}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-activedescendant={open && active !== -1 ? optionId(active) : undefined}
                aria-label={ariaLabel}
                aria-describedby={field.describedBy}
                aria-invalid={field.invalid || undefined}
                disabled={disabled}
                onClick={() => (open ? close() : openList(selectedIndex))}
                onKeyDown={onKeyDown}
                className={cn(
                    'items-center font-[family-name:var(--font-body)] leading-[1.35]',
                    'cursor-pointer text-left transition-colors duration-100',
                    stretch ? 'flex w-full' : 'inline-flex max-w-full',
                    triggerSize[effectiveSize],
                    styles.rest,
                    open && styles.open,
                    styles.focus,
                    disabledStyles,
                    className,
                )}
            >
                {icon ? (
                    <Icon
                        name={icon}
                        size="sm"
                        tone={surface === 'onDark' ? 'onDarkMuted' : 'muted'}
                    />
                ) : null}

                <span className="min-w-0 flex-1 truncate">
                    {prefix ? (
                        <span
                            className={
                                surface === 'onDark'
                                    ? 'text-[var(--text-on-dark-3)]'
                                    : 'text-[var(--text-tertiary-safe)]'
                            }
                        >
                            {`${prefix}: `}
                        </span>
                    ) : null}
                    <span
                        className={cn(
                            selected === null &&
                                (surface === 'onDark'
                                    ? 'text-[var(--text-on-dark-3)]'
                                    : 'text-[var(--text-tertiary-safe)]'),
                        )}
                    >
                        {label}
                    </span>
                </span>

                <Icon
                    name="chevronDown"
                    size="sm"
                    tone={surface === 'onDark' ? 'onDarkMuted' : 'muted'}
                    className={cn(open && 'rotate-180')}
                />
            </button>

            {open && layerRoot
                ? createPortal(
                      <div
                          ref={listRef}
                          id={listId}
                          role="listbox"
                          aria-label={ariaLabel}
                          aria-labelledby={ariaLabel ? undefined : field.labelId}
                          tabIndex={-1}
                          className={cn(
                              'fixed top-0 left-0 z-[65] flex max-w-[min(420px,calc(100vw-16px))] flex-col gap-[2px] overflow-auto overscroll-contain',
                              'rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] p-1 outline-none',
                          )}
                          style={{ boxShadow: 'var(--shadow-popover)' }}
                      >
                          {options.map((option, index) => {
                              const isSelected = index === selectedIndex;
                              const isActive = index === active;

                              return (
                                  <div
                                      key={option.value}
                                      id={optionId(index)}
                                      role="option"
                                      aria-label={
                                          option.hint
                                              ? `${option.label}, ${option.hint}`
                                              : undefined
                                      }
                                      aria-selected={isSelected}
                                      aria-disabled={option.disabled || undefined}
                                      onPointerDown={(event) => event.preventDefault()}
                                      onPointerMove={() => {
                                          if (!option.disabled && !isActive) setActive(index);
                                      }}
                                      onClick={() => choose(index)}
                                      className={cn(
                                          'flex cursor-pointer items-center gap-[10px] rounded-[6px] px-[10px] py-[7px]',
                                          'font-[family-name:var(--font-body)] leading-[1.3] text-[var(--text-primary)]',
                                          optionSize[effectiveSize],
                                          isActive && 'bg-[var(--surface-row-hover)]',
                                          isSelected && 'font-medium text-[var(--accent-dark)]',
                                          isSelected && isActive && 'bg-[var(--accent-tint)]',
                                          option.disabled && 'cursor-not-allowed opacity-50',
                                      )}
                                  >
                                      <span className="flex min-w-0 flex-1 flex-col">
                                          <span className="truncate">{option.label}</span>
                                          {option.hint ? (
                                              <span className="truncate text-[12px] font-normal text-[var(--text-tertiary-safe)]">
                                                  {option.hint}
                                              </span>
                                          ) : null}
                                      </span>
                                      <Icon
                                          name="check"
                                          size="sm"
                                          tone="brand"
                                          className={cn(!isSelected && 'invisible')}
                                      />
                                  </div>
                              );
                          })}

                          {action ? (
                              <>
                                  <div
                                      aria-hidden="true"
                                      className="mx-[6px] my-[2px] h-px shrink-0 bg-[var(--border-subtle)]"
                                  />
                                  <div
                                      id={optionId(actionIndex)}
                                      role="option"
                                      aria-selected={false}
                                      onPointerDown={(event) => event.preventDefault()}
                                      onPointerMove={() => {
                                          if (active !== actionIndex) setActive(actionIndex);
                                      }}
                                      onClick={() => choose(actionIndex)}
                                      className={cn(
                                          'flex cursor-pointer items-center gap-[10px] rounded-[6px] px-[10px] py-[7px]',
                                          'font-[family-name:var(--font-body)] leading-[1.3] font-medium text-[var(--accent-dark)]',
                                          optionSize[effectiveSize],
                                          active === actionIndex && 'bg-[var(--surface-row-hover)]',
                                      )}
                                  >
                                      {action.icon ? (
                                          <Icon name={action.icon} size="sm" tone="brand" />
                                      ) : null}
                                      <span className="min-w-0 flex-1 truncate">
                                          {action.label}
                                      </span>
                                  </div>
                              </>
                          ) : null}
                      </div>,
                      layerRoot,
                  )
                : null}
        </>
    );
}
